<script setup>
import { ref, watch, nextTick } from 'vue';
const props = defineProps({ error: { type: Object, default: null } });
const emit = defineEmits(['close', 'duplicates', 'open-import']);
const dialog = ref(null);
watch(() => props.error, async (value) => {
  await nextTick();
  if (value && !dialog.value.open) dialog.value.showModal();
  if (!value && dialog.value.open) dialog.value.close();
});
</script>

<template>
  <dialog ref="dialog" aria-labelledby="timesheet-save-error-title" class="w-[min(56rem,95vw)] max-h-[85vh] rounded-xl border border-slate-300 bg-white p-0 text-slate-900 shadow-xl backdrop:bg-black/50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100" @close="emit('close')">
    <div v-if="error" class="space-y-4 p-5">
      <h2 id="timesheet-save-error-title" class="text-lg font-bold">Timesheet not saved</h2>
      <p class="text-sm">{{ error.file }} · import #{{ error.recordId }}</p>
      <ul class="list-disc space-y-2 pl-5 text-sm"><li v-for="(message, index) in error.messages" :key="index" class="whitespace-pre-line">{{ message }}</li></ul>
      <p class="text-sm font-semibold">Your edits are still in the table. Keep this page open until you have saved or discarded them.</p>
      <section v-for="conflict in error.conflicts" :key="conflict.claim_id" class="space-y-2 rounded border border-amber-300 p-3 text-sm dark:border-amber-700">
        <h3 class="font-bold">Conflicting claim #{{ conflict.claim_id }} · timesheet #{{ conflict.timesheet_id }}</h3>
        <p>{{ conflict.staff }} · {{ conflict.reference || 'Job reference unavailable' }} · {{ conflict.date || 'Date missing' }}</p>
        <p>Paid times: {{ conflict.start || '?' }}–{{ conflict.finish || '?' }} · {{ conflict.hours ?? '?' }} hours</p>
        <p v-for="source in conflict.imports" :key="source.id">
          {{ source.file }} · import #{{ source.id }} · {{ source.status }} · payroll run #{{ source.run_id || '?' }}
          <a v-if="source.source_url" :href="source.source_url" target="_blank" rel="noopener" class="ml-2 text-sky-700 underline dark:text-sky-300">View original</a>
          <button v-if="Number(source.run_id) === Number(error.runId)" type="button" class="ml-2 text-sky-700 underline dark:text-sky-300" @click="emit('open-import', source)">Open linked import</button>
        </p>
        <p v-if="!conflict.imports.length">No source import is linked to this claim; its origin needs investigation.</p>
        <a v-if="!conflict.imports.length && conflict.source_url" :href="conflict.source_url" target="_blank" rel="noopener" class="text-sky-700 underline dark:text-sky-300">View claim document: {{ conflict.source_file }}</a>
        <p v-for="approval in conflict.approvals" :key="approval.id">Approval #{{ approval.id }} · payroll run #{{ approval.payroll_run_id }} · {{ approval.status }}</p>
      </section>
      <section v-if="error.steps.length" class="text-sm">
        <h3 class="mb-2 font-semibold">How to resolve this</h3>
        <ol class="list-decimal space-y-2 pl-5"><li v-for="step in error.steps" :key="step">{{ step }}</li></ol>
      </section>
      <div class="flex flex-wrap justify-end gap-3">
        <button v-if="error.conflicts.length" type="button" class="rounded border border-slate-400 px-3 py-2 text-sm" @click="emit('duplicates')">Compare duplicate submissions</button>
        <button type="button" autofocus class="rounded bg-sky-700 px-3 py-2 text-sm font-semibold text-white" @click="dialog.close()">Return to editing</button>
      </div>
    </div>
  </dialog>
</template>
